import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, TooltipProps } from 'recharts';
import { Empty } from '@frontend/app/refresh-components';
import { ChartLineIcon } from '@revfluence/fresh-icons/solid/esm';
import { ChartContainer, ChartTooltip } from '@/shadcn/components/ui/chart';
import { formatNumberDefault } from '../../../../../utils/formatNumber';
import { tickFormatters } from '../../../../../../../app/components/Charts/utils/tickFormatters';

// Define a generic data structure for the chart
export interface SocialStatsChartDataItem {
  xValue: string | number; // e.g., date or category
  yValue: number;
  [key: string]: any; // Allow other properties if needed for tooltip or other features
}

interface SocialStatsChartViewProps {
  data?: SocialStatsChartDataItem[];
  activeTabKey: 'engagements' | 'reach'; // Or a more generic string if tabs change
  lineColor?: string;
}

// Custom Tooltip Content
const CustomChartTooltipContent = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (active && payload && payload.length && payload[0].payload) {
    const dataItem = payload[0].payload as SocialStatsChartDataItem;
    const formattedDate = tickFormatters.date(label);

    return (
      <div className="p-2 bg-background border text-foreground border-border rounded-md shadow-lg text-sm">
        <p className="label font-semibold">{formattedDate}</p>
        <p className="intro">{`Value: ${formatNumberDefault(dataItem.yValue)}`}</p>
      </div>
    );
  }
  return null;
};

export const SocialStatsChartView: React.FC<SocialStatsChartViewProps> = ({
  data,
  activeTabKey,
  lineColor = '#6C77DE',
}) => {
  if (!data || data.length === 0) {
    return (
      <Empty
        icon={ChartLineIcon} // Use an appropriate icon
        title={`No ${activeTabKey} data`}
        description={`Data for ${activeTabKey} will appear here once available.`}
        size="lg"
      />
    );
  }

  const chartConfig = {
    [activeTabKey]: {
      label: activeTabKey.charAt(0).toUpperCase() + activeTabKey.slice(1),
      color: lineColor,
    },
  };

  return (
    <ChartContainer config={chartConfig} className="w-full h-[200px]">
      <LineChart
        data={data}
        margin={{
          top: 5,
          right: 20,
          left: 0,
          bottom: 5,
        }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          vertical={false}
          stroke="#E5E7EB"
          strokeWidth={1}
        />
        <XAxis
          dataKey="xValue"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={tickFormatters.date}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={(value: number) => String(formatNumberDefault(value))}
          width={50}
        />
        <ChartTooltip
          cursor={false}
          content={<CustomChartTooltipContent />}
        />
        <Line
          dataKey="yValue"
          type="monotone"
          stroke={lineColor}
          strokeWidth={4}
          dot={{
            fill: lineColor,
            r: 0,
          }}
          activeDot={{
            r: 6,
            strokeWidth: 1,
            fill: 'white',
            stroke: lineColor,
          }}
          name={chartConfig[activeTabKey].label}
        />
      </LineChart>
    </ChartContainer>
  );
};

SocialStatsChartView.displayName = 'SocialStatsChartView';
